import React, { useState, useMemo } from "react";
import Select from "react-select";
import Swal from "sweetalert2";
import moment from "moment";
import { useTable, useSortBy, useGlobalFilter } from "react-table";

import apiClient from "../utils/apiClient";
import { SERVER_BASE } from "../config/server";
import DateRangePickerComponent from "./CustomDateRangePicker";

const DataTable = ({
  columns,
  data,
  expandedRows,
  toggleItems,
  savingVoucherId,
  handleSaveVoucher,
  updateTagRow,
  addTagRow,
  removeTagRow,
}) => {
  const [selectedItems, setSelectedItems] = useState([]);

  const getItemLabel = (item) =>
    item.itemName || item.item || item.printName || item.name || "";

  const itemOptions = useMemo(() => {
    const namesSet = new Set();

    data.forEach((voucher) => {
      const items =
        voucher.interDepartmentIssueSlipItems || voucher.items || [];

      items.forEach((item) => {
        const label = getItemLabel(item);
        if (label) namesSet.add(label);
      });
    });

    return Array.from(namesSet)
      .sort((a, b) => a.localeCompare(b))
      .map((name) => ({ label: name, value: name }));
  }, [data]);

  const filteredData = useMemo(() => {
    if (!selectedItems || selectedItems.length === 0) return data;

    const selectedValues = selectedItems.map((opt) => opt.value);

    return data.filter((voucher) => {
      const items =
        voucher.interDepartmentIssueSlipItems || voucher.items || [];

      return items.some((item) => selectedValues.includes(getItemLabel(item)));
    });
  }, [data, selectedItems]);

  const tableInstance = useTable(
    {
      columns,
      data: filteredData,
    },
    useGlobalFilter,
    useSortBy,
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state,
    setGlobalFilter,
  } = tableInstance;

  return (
    <>
      <div className="d-flex justify-content-between align-items-end mb-3 gap-3">
        <input
          className="form-control w-50"
          placeholder="Search..."
          value={state.globalFilter || ""}
          onChange={(e) => setGlobalFilter(e.target.value)}
        />

        <div style={{ minWidth: 300, flex: 1 }}>
          <label className="form-label mb-1 small">Filter by Item</label>

          <Select
            isMulti
            options={itemOptions}
            value={selectedItems}
            onChange={(value) => setSelectedItems(value || [])}
            placeholder="Select item(s)..."
            isClearable
          />
        </div>

        <strong className="text-nowrap">Showing {rows.length} Records</strong>
      </div>

      <table
        className="table table-bordered table-striped"
        {...getTableProps()}
      >
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                  {column.render("Header")}

                  {column.isSorted ? (column.isSortedDesc ? " 🔽" : " 🔼") : ""}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);

            return (
              <React.Fragment key={row.original.id}>
                <tr {...row.getRowProps()}>
                  {row.cells.map((cell) => (
                    <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                  ))}
                </tr>

                {expandedRows[row.original.id] && (
                  <tr>
                    <td colSpan={columns.length} className="bg-light">
                      <div className="table-responsive">
                        <table className="table table-bordered table-sm mb-0">
                          <thead className="table-light">
                            <tr>
                              <th width="25%">Item Name</th>
                              <th width="8%">Qty</th>
                              <th width="15%">Approval Date</th>
                              <th width="20%">Approval No</th>
                              <th width="10%">Tag Qty</th>
                              <th width="12%">Value</th>
                              <th width="10%">Action</th>
                            </tr>
                          </thead>

                          <tbody>
                            {row.original.items.map((item, itemIndex) => (
                              <React.Fragment
                                key={`${item.itemId || item.id}-${itemIndex}`}
                              >
                                {item.taggingRows.map((tagRow, rowIndex) => (
                                  <tr
                                    key={`${item.itemId || item.id}-${rowIndex}`}
                                  >
                                    {rowIndex === 0 && (
                                      <>
                                        <td
                                          rowSpan={item.taggingRows.length}
                                          className="align-middle fw-bold"
                                        >
                                          {item.itemName ||
                                            item.item ||
                                            item.printName}
                                        </td>

                                        <td
                                          rowSpan={item.taggingRows.length}
                                          className="align-middle text-center"
                                        >
                                          {item.quantity ||
                                            item.receivedQty ||
                                            item.qty ||
                                            item.issuedQuantity}
                                        </td>
                                      </>
                                    )}

                                    <td>
                                      <input
                                        type="date"
                                        className="form-control form-control-sm"
                                        value={tagRow.approvalDate}
                                        onChange={(e) =>
                                          updateTagRow(
                                            row.original.id,
                                            itemIndex,
                                            rowIndex,
                                            "approvalDate",
                                            e.target.value,
                                          )
                                        }
                                      />
                                    </td>

                                    <td>
                                      <input
                                        className="form-control form-control-sm"
                                        value={tagRow.approvalNumber}
                                        onChange={(e) =>
                                          updateTagRow(
                                            row.original.id,
                                            itemIndex,
                                            rowIndex,
                                            "approvalNumber",
                                            e.target.value,
                                          )
                                        }
                                      />
                                    </td>

                                    <td>
                                      <input
                                        type="number"
                                        className="form-control form-control-sm"
                                        value={tagRow.qty}
                                        onChange={(e) =>
                                          updateTagRow(
                                            row.original.id,
                                            itemIndex,
                                            rowIndex,
                                            "qty",
                                            e.target.value,
                                          )
                                        }
                                      />
                                    </td>

                                    <td>
                                      <input
                                        type="number"
                                        className="form-control form-control-sm"
                                        value={tagRow.value}
                                        onChange={(e) =>
                                          updateTagRow(
                                            row.original.id,
                                            itemIndex,
                                            rowIndex,
                                            "value",
                                            e.target.value,
                                          )
                                        }
                                      />
                                    </td>

                                    <td>
                                      <div className="d-flex justify-content-center gap-1">
                                        <button
                                          type="button"
                                          className="btn btn-danger btn-sm"
                                          disabled={
                                            item.taggingRows.length === 1
                                          }
                                          onClick={() =>
                                            removeTagRow(
                                              row.original.id,
                                              itemIndex,
                                              rowIndex,
                                            )
                                          }
                                        >
                                          -
                                        </button>

                                        <button
                                          type="button"
                                          className="btn btn-primary btn-sm"
                                          onClick={() =>
                                            addTagRow(
                                              row.original.id,
                                              itemIndex,
                                            )
                                          }
                                        >
                                          +
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </React.Fragment>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </>
  );
};

const VoucherTagging = () => {
  const token = localStorage.getItem("token");
  const email = localStorage.getItem("email");
  const code = localStorage.getItem("code");

  const [voucherType, setVoucherType] = useState(null);

  const [dateRange, setDateRange] = useState([null, null]);

  const [voucherData, setVoucherData] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [savingVoucherId, setSavingVoucherId] = useState(null);

  const voucherOptions = [
    {
      label: "Receipt Voucher",
      value: "Receipt Voucher",
    },
    {
      label: "Delivery Challan",
      value: "Delivery Challan",
    },
    {
      label: "Event Transaction",
      value: "Event Transaction",
    },
    {
      label: "Addition",
      value: "Addition",
    },
    {
      label: "Deduction",
      value: "Deduction",
    },
    {
      label: "Addition Ledger",
      value: "Addition Ledger",
    },
    {
      label: "Deduction Ledger",
      value: "Deduction Ledger",
    },
    {
      label: "Issue Slip",
      value: "Issue Slip",
    },
  ];

  const getVoucherDate = (voucher) => {
    if (voucherType?.value === "Issue Slip") {
      return voucher.interDepartmentTransfer?.issuedDate || "";
    }

    return voucher.documentDate || voucher.date || voucher.entryDate;
  };

  const getVoucherItems = (voucher) => {
    if (voucherType?.value === "Issue Slip") {
      return voucher.interDepartmentIssueSlipItems || [];
    }

    return voucher.items || [];
  };

  const getVoucherNumber = (voucher) => {
    if (voucherType?.value === "Receipt Voucher") return voucher.rvNo;

    if (voucherType?.value === "Delivery Challan")
      return `${voucher.prefix || ""}${voucher.refNo || ""}`;

    if (
      voucherType?.value === "Addition" ||
      voucherType?.value === "Deduction" ||
      voucherType?.value === "Addition Ledger" ||
      voucherType?.value === "Deduction Ledger"
    )
      return voucher.draftCode;

    if (voucherType?.value === "Event Transaction")
      return voucher.transactionCode;

    if (voucherType?.value === "Issue Slip") {
      return (
        voucher.issueSlipCode ||
        voucher.slipNo ||
        voucher.issueNo ||
        voucher.code ||
        voucher.id
      );
    }

    return "";
  };

  const getDepartment = (voucher) => {
    if (
      voucherType?.value === "Addition" ||
      voucherType?.value === "Deduction" ||
      voucherType?.value === "Event Transaction"
    ) {
      return voucher.department || "";
    }

    if (voucherType?.value === "Issue Slip") {
      return voucher.receivingDepartment || "";
    }

    return "";
  };

  const getPurpose = (voucher) => {
    if (voucherType?.value === "Event Transaction") {
      return voucher.event || "";
    }

    if (
      voucherType?.value === "Addition" ||
      voucherType?.value === "Deduction" ||
      voucherType?.value === "Addition Ledger" ||
      voucherType?.value === "Deduction Ledger"
    ) {
      return voucher.purpose || "";
    }

    if (voucherType?.value === "Issue Slip") {
      return `Issue Slip - ${voucher.issuingDepartment || ""}`;
    }

    return "";
  };

  const buildVoucherData = (rows) => {
    return rows.map((voucher) => {
      const taggedItems = voucher?.taggingData?.taggedItems || [];

      // Issue Slip items come from the Sequelize association:
      // InterDepartmentIssueSlip.hasMany(InterDepartmentIssueSlipItems)
      const sourceItems =
        voucherType?.value === "Issue Slip"
          ? voucher.interDepartmentIssueSlipItems || []
          : voucher.items || [];

      const items = sourceItems.map((item) => {
        const existingItem = taggedItems.find(
          (x) => String(x.itemId) === String(item.itemId),
        );

        let taggingRows = [];

        if (existingItem && existingItem.tags?.length > 0) {
          taggingRows = existingItem.tags.map((tag) => ({
            approvalDate: tag.approvalDate || "",
            approvalNumber: tag.approvalNumber || "",
            qty: tag.qty || "",
            value: tag.value || "",
          }));
        } else {
          taggingRows = [
            {
              approvalDate: "",
              approvalNumber: "",
              qty: "",
              value: "",
            },
          ];
        }

        return {
          ...item,
          taggingRows,
        };
      });

      return {
        ...voucher,

        // Normalize Issue Slip items so the rest of the component
        // can continue using voucher.items
        items,

        // Issue Slip date comes from InterDepartmentTransfer
        ...(voucherType?.value === "Issue Slip" && {
          issueSlipDate: voucher.interDepartmentTransfer?.issuedDate || "",
        }),
      };
    });
  };

  const fetchVoucherData = async () => {
    try {
      if (!voucherType) {
        Swal.fire("Required", "Please select Voucher Type", "warning");
        return;
      }
      const [start, end] = dateRange;
      const response = await apiClient.post(
        SERVER_BASE + "/getVoucherTaggingData",
        {
          voucherType: voucherType.value,
          startDate: start,
          endDate: end,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setVoucherData(buildVoucherData(response?.data?.data || []));
    } catch (error) {
      console.error(error);

      Swal.fire("Error", "Failed to fetch vouchers", "error");
    }
  };

  const toggleItems = (voucherId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [voucherId]: !prev[voucherId],
    }));
  };

  const addTagRow = (voucherId, itemIndex) => {
    setVoucherData((prev) =>
      prev.map((voucher) => {
        if (voucher.id !== voucherId) return voucher;

        return {
          ...voucher,
          items: voucher.items.map((item, idx) => {
            if (idx !== itemIndex) return item;

            return {
              ...item,
              taggingRows: [
                ...item.taggingRows,
                {
                  approvalDate: "",
                  approvalNumber: "",
                  qty: "",
                  value: "",
                },
              ],
            };
          }),
        };
      }),
    );
  };

  const removeTagRow = (voucherId, itemIndex, rowIndex) => {
    setVoucherData((prev) =>
      prev.map((voucher) => {
        if (voucher.id !== voucherId) return voucher;

        return {
          ...voucher,
          items: voucher.items.map((item, idx) => {
            if (idx !== itemIndex) return item;

            if (item.taggingRows.length === 1) return item;

            return {
              ...item,
              taggingRows: item.taggingRows.filter((_, i) => i !== rowIndex),
            };
          }),
        };
      }),
    );
  };

  const updateTagRow = (voucherId, itemIndex, rowIndex, field, value) => {
    setVoucherData((prev) =>
      prev.map((voucher) => {
        if (voucher.id !== voucherId) return voucher;

        return {
          ...voucher,
          items: voucher.items.map((item, idx) => {
            if (idx !== itemIndex) return item;

            return {
              ...item,
              taggingRows: item.taggingRows.map((row, i) =>
                i === rowIndex
                  ? {
                      ...row,
                      [field]: value,
                    }
                  : row,
              ),
            };
          }),
        };
      }),
    );
  };
  const handleSaveVoucher = async (voucher) => {
    try {
      setSavingVoucherId(voucher.id);

      const payloadItems = [];
      let hasAnyTagging = false;

      for (const item of voucher.items) {
        let totalTaggedQty = 0;
        const validTags = [];

        for (const row of item.taggingRows) {
          const qty = Number(row.qty || 0);
          const value = Number(row.value || 0);

          // Ignore completely blank rows
          if (
            qty <= 0 &&
            value <= 0 &&
            !row.approvalDate &&
            !row.approvalNumber
          ) {
            continue;
          }

          hasAnyTagging = true;

          if (!row.approvalDate) {
            Swal.fire({
              toast: true,
              position: "top-end",
              icon: "error",
              title: `Approval Date is required for ${
                item.itemName || item.item || item.printName
              }`,
              showConfirmButton: false,
              timer: 3000,
            });
            return;
          }

          if (!row.approvalNumber || !row.approvalNumber.trim()) {
            Swal.fire({
              toast: true,
              position: "top-end",
              icon: "error",
              title: `Approval Number is required for ${
                item.itemName || item.item || item.printName
              }`,
              showConfirmButton: false,
              timer: 3000,
            });
            return;
          }

          if (qty <= 0) {
            Swal.fire({
              toast: true,
              position: "top-end",
              icon: "error",
              title: `Tag Qty is required for ${
                item.itemName || item.item || item.printName
              }`,
              showConfirmButton: false,
              timer: 3000,
            });
            return;
          }

          if (value <= 0) {
            Swal.fire({
              toast: true,
              position: "top-end",
              icon: "error",
              title: `Tag Value is required for ${
                item.itemName || item.item || item.printName
              }`,
              showConfirmButton: false,
              timer: 3000,
            });
            return;
          }

          totalTaggedQty += qty;

          validTags.push({
            approvalDate: row.approvalDate,
            approvalNumber: row.approvalNumber.trim(),
            qty,
            value,
          });
        }

        const itemQty = Number(
          item.quantity ||
            item.receivedQty ||
            item.qty ||
            item.issuedQuantity ||
            0,
        );

        if (totalTaggedQty > itemQty) {
          Swal.fire({
            toast: true,
            position: "top-end",
            icon: "error",
            title: `${
              item.itemName || item.item || item.printName
            } tagged qty cannot exceed ${itemQty}`,
            showConfirmButton: false,
            timer: 4000,
          });
          return;
        }

        if (validTags.length > 0) {
          payloadItems.push({
            itemId: item.itemId,
            itemName: item.itemName || item.item || item.printName,
            quantity:
              item.quantity ||
              item.receivedQty ||
              item.qty ||
              item.issuedQuantity,
            uom: item.uom,
            tags: validTags,
          });
        }
      }

      if (!hasAnyTagging) {
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "error",
          title: "Please enter at least one tagging row",
          showConfirmButton: false,
          timer: 3000,
        });
        return;
      }

      const payload = {
        voucherType: voucherType.value,

        voucherId: voucher.id,

        voucherNo: getVoucherNumber(voucher),

        centerCode: voucher.centerCode || code,

        createdBy: email || code || "",

        taggedItems: payloadItems,
      };

      const response = await apiClient.post(
        SERVER_BASE + "/saveVoucherTagging",
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: response?.data?.message || "Voucher tagging saved successfully",
        showConfirmButton: false,
        timer: 3000,
      });

      await fetchVoucherData();
    } catch (error) {
      console.error(error);

      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title:
          error?.response?.data?.message || "Failed to save voucher tagging",
        showConfirmButton: false,
        timer: 3000,
      });
    } finally {
      setSavingVoucherId(null);
    }
  };
  const columns = useMemo(
    () => [
      {
        Header: "S.No",
        Cell: ({ row }) => row.index + 1,
      },
      {
        Header: "Date",
        accessor: (row) => getVoucherDate(row),
        Cell: ({ value }) => (value ? moment(value).format("DD-MMM-YY") : ""),
      },
      {
        Header: "Voucher No",
        accessor: (row) => getVoucherNumber(row),
      },
      {
        Header: "Department",
        accessor: (row) => getDepartment(row),
      },
      {
        Header: "Event / Purpose",
        accessor: (row) => getPurpose(row),
      },
      {
        Header: "Items",
        id: "items",
        Cell: ({ row }) => (
          <button
            className="btn btn-sm btn-outline-success"
            onClick={() => toggleItems(row.original.id)}
          >
            {expandedRows[row.original.id] ? "Hide Items" : "Show Items"}
          </button>
        ),
      },
      {
        Header: "Save",
        id: "save",
        Cell: ({ row }) => (
          <button
            className="btn btn-sm btn-primary"
            disabled={savingVoucherId === row.original.id}
            onClick={() => handleSaveVoucher(row.original)}
          >
            {savingVoucherId === row.original.id ? "Saving..." : "Save"}
          </button>
        ),
      },
    ],
    [expandedRows, savingVoucherId, voucherType],
  );
  return (
    <div className="formcontainer mt-5">
      <h4 className="mb-1"> Tag Item Approvals</h4>

      <div className="row align-items-end">
        <div className="col-md-3">
          <label className="form-label">Voucher Type</label>

          <Select
            options={voucherOptions}
            value={voucherType}
            onChange={(value) => {
              setVoucherType(value);
              setVoucherData([]);
              setExpandedRows({});
            }}
            isClearable
          />
        </div>

        <div className="col-md-3">
          <label className="form-label">Date Range</label>

          <DateRangePickerComponent value={dateRange} onChange={setDateRange} />
        </div>

        <div className="col-md-2">
          <button
            className="btn btn-primary w-100"
            disabled={!voucherType}
            onClick={fetchVoucherData}
          >
            Get Data
          </button>
        </div>
      </div>

      {voucherData.length > 0 && (
        <div className="mt-4">
          <DataTable
            columns={columns}
            data={voucherData}
            expandedRows={expandedRows}
            toggleItems={toggleItems}
            savingVoucherId={savingVoucherId}
            handleSaveVoucher={handleSaveVoucher}
            updateTagRow={updateTagRow}
            addTagRow={addTagRow}
            removeTagRow={removeTagRow}
          />
        </div>
      )}

      {voucherType && voucherData.length === 0 && (
        <div className="text-center mt-4">
          <small className="text-muted">No vouchers found</small>
        </div>
      )}
    </div>
  );
};

export default VoucherTagging;
